<template>
  <!--
    Dropdown menu of states, generally at the top of the page like grantee select (there should be only one of these on a page). Use simpleVersion prop for no styling (for use somewhere else in the page).
  -->
  <div>
    <div v-if="lscDotGovVersion === true" class="lsc-dot-gov-styling">
      <select 
        class="lsc-gov-form-select" 
        v-model="selectedState"
        @change="regularSelectChange($event)"
        :id="selectMenuId"
      >
        <option v-for="state in states" :key="state.value" :value="state.value">{{ state.text }}</option>
      </select>
    </div>
    <div v-else-if="simpleVersion === true" class="simple-state-select">
      <v-autocomplete
        v-model="selectedState"
        :items="states"
        :menu-props="{ maxHeight: '400' }"
        label=""
        :hint="hint"
        persistent-hint
        @change="setSelectedState"
        :id="selectMenuId"
      >
      </v-autocomplete>
    </div>
    <div v-else>
      <div id="g-select-wrapper" :class="noBottomPadding === true ? 'no-bottom-padding' : ''">
        <div id="g-select">
          <div class="g-select-fill">
            <!-- The actual select menu -->
            <div id="main-select">
              <v-select
                v-model="selectedState"
                :items="states"
                :menu-props="{ maxHeight: '400' }"
                placeholder="Select a state"
                persistent-hint
                ref="stateDropdown"
                @change="setSelectedState"
                :id="selectMenuId"
              >
              </v-select>
            </div>
          </div>
          <!-- The button (see OrgSelectMenu component; is repurposed from a prior v-dialog version :: to be converted to proper button) -->
          <div class="g-select-text">
            <div class="text-center">
              <v-dialog
                v-model="dialog"
                width="500"
                scrollable
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="red lighten-1"
                    text
                    v-bind="attrs"
                    v-on="on"
                    class="grantee-select-btn"
                  >
                    <span class="grantee-select-btn-outer"><span class="grantee-select-btn-inner">{{ selectedStateName || 'Select state' }}</span> <span class="grantee-select-btn-inner">&#9660;</span></span>
                  </v-btn>
                </template>
              </v-dialog>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script> 
export default {
  name: 'StateSelect',

  props: {
    selectMenuId: { type: String, required: false, default: 'state-select' }, // Pass in an ID for the select menu, such as to associate a label with this select menu
    simpleVersion: { type: Boolean, required: false, default: false }, // No styling. Generally for use deeper within a page, not as the main state select below the title.
    lscDotGovVersion: { type: Boolean, required: false, default: false }, //LSC.gov styling
    includePR: { type: Boolean, default: true },
    includeAS: { type: Boolean, default: true },
    includeVI: { type: Boolean, default: true },
    includeGU: { type: Boolean, default: true },
    includeMP: { type: Boolean, default: true },
    allOption: { type: Boolean, required: false, default: false },
    statePassedAsProp: { type: String, required: false, default: null },
    customStates: { type: Array, required: false, default: function() { return [] } }, // list of state fips to show in the dropdown
    noBottomPadding: { type: Boolean, required: false, default: false }, // Removes the default 2 rem padding if true
    neverUpdateUrlParam: { type: Boolean, required: false, default: false } // use true to read the URL parameters on page load but not change it when the dropdown changes (which updates the route and jumps to the top of the page)
  },

  data () {
    return {
      dialog: false,
      selectedState: null,
      hint: 'Select a state or D.C.',
      states: [
        {text: 'Alabama', value: '01'},
        {text: 'Alaska', value: '02'},
        {text: 'American Samoa', value: '60'},
        {text: 'Arizona', value: '04'},
        {text: 'Arkansas', value: '05'},
        {text: 'California', value: '06'},
        {text: 'Colorado', value: '08'},
        {text: 'Connecticut', value: '09'},
        {text: 'Delaware', value: '10'},
        {text: 'District of Columbia', value: '11'},
        {text: 'Florida', value: '12'},
        {text: 'Georgia', value: '13'},
        {text: 'Guam', value: '66'},
        {text: 'Hawaii', value: '15'},
        {text: 'Idaho', value: '16'},
        {text: 'Illinois', value: '17'},
        {text: 'Indiana', value: '18'},
        {text: 'Iowa', value: '19'},
        {text: 'Kansas', value: '20'},
        {text: 'Kentucky', value: '21'},
        {text: 'Louisiana', value: '22'},
        {text: 'Maine', value: '23'},
        {text: 'Maryland', value: '24'},
        {text: 'Massachusetts', value: '25'},
        {text: 'Michigan', value: '26'},
        {text: 'Micronesia', value: '69'},
        {text: 'Minnesota', value: '27'},
        {text: 'Mississipi', value: '28'},
        {text: 'Missouri', value: '29'},
        {text: 'Montana', value: '30'},
        {text: 'Nebraska', value: '31'},
        {text: 'Nevada', value: '32'},
        {text: 'New Hampshire', value: '33'},
        {text: 'New Jersey', value: '34'},
        {text: 'New Mexico', value: '35'},
        {text: 'New York', value: '36'},
        {text: 'North Carolina', value: '37'},
        {text: 'North Dakota', value: '38'},
        {text: 'Ohio', value: '39'},
        {text: 'Oklahoma', value: '40'},
        {text: 'Oregon', value: '41'},
        {text: 'Pennsylvania', value: '42'},
        {text: 'Puerto Rico', value: '72'},
        {text: 'Rhode Island', value: '44'},
        {text: 'South Carolina', value: '45'},
        {text: 'South Dakota', value: '46'},
        {text: 'Tennessee', value: '47'},
        {text: 'Texas', value: '48'},
        {text: 'Utah', value: '49'},
        {text: 'Vermont', value: '50'},
        {text: 'Virgin Islands', value: '78'},
        {text: 'Virginia', value: '51'},
        {text: 'Washington', value: '53'},
        {text: 'West Virginia', value: '54'},
        {text: 'Wisconsin', value: '55'},
        {text: 'Wyoming', value: '56'}
      ]
    }
  },

  computed: {
    selectedStateName () { 
      if (this.selectedState !== '' && this.selectedState !== null) {
        return this.states.filter(x => x.value === this.selectedState)[0].text
      } else {
        return ''
      }
    },
    statesList () {
      return this.states.map(x => x.value)
    }
  },

  async created () {
    if (this.allOption === true) {
      this.states.unshift({text: 'All States', value: '*'})
    } else {
      this.states = this.states.filter(x => x.value !== '*')
    }

    // FILTER STATES LIST TO INCLUDE PR AND TERRITORIES OR NOT, AS APPROPRIATE FOR THE RESOURCE
    if (this.includePR === false) {
      this.states = this.states.filter(function(x) { return x.value !== '72' })
    }
    if (this.includeAS === false) {
      this.states = this.states.filter(function(x) { return x.value !== '60' })
    }
    if (this.includeVI === false) {
      this.states = this.states.filter(function(x) { return x.value !== '78' })
    }
    if (this.includeMP === false) {
      this.states = this.states.filter(function(x) { return x.value !== '69' })
    }
    if (this.includeGU === false) {
      this.states = this.states.filter(function(x) { return x.value !== '66' })
    }

    // FILTER STATES LIST FOR CUSTOM ARRAY, IF PROVIDED
    if (this.customStates.length > 0) {
      const c = this.customStates
      this.states = this.states.filter(function(x) { return c.indexOf(x.value) > -1 })
    }

    // CHANGE THE HINT TEXT DEPENDING ON WHICH TERRITORIES ARE INCLUDED
    if (this.includePR === true && this.includeAS === false && this.includeGU === false && this.includeMP === false && this.includeVI === false) {
      this.hint = "Select a state, D.C., or Puerto Rico"
    }    

    if (this.includePR === true && (this.includeAS === true || this.includeGU === true || this.includeMP === true || this.includeVI === true)) {
      this.hint = "Select a state, D.C., or territory"
    }
  },

  async mounted () {
    // Set selected state per URL parameter if provided
    if (this.$route.query.state && this.statesList.indexOf(this.$route.query.state) > -1) {
      this.selectedState = this.$route.query.state
      this.$emit('stateSelected', this.$route.query.state)
    }

    // If state passed in as statePassedAsProp prop
    if (this.statePassedAsProp !== null && this.statePassedAsProp.length > 0) {
      this.selectedState = this.statePassedAsProp
    }
  },

  methods: {
    regularSelectChange (event) {
      this.setSelectedState(event.target.value)
    },
    setSelectedState (val) {
      this.selectedState = val
      if (this.neverUpdateUrlParam === false) {
        this.$router.push({ path: this.$route.path, query: { ...this.$route.query, state: val }}).catch(()=>{})
      }
      this.$emit('stateSelected', val)
    }
  },

  watch: {
    $route (to) {
      // To trigger any refreshes from the state query parameter on back/forward button history change
      if (to.query.state !== this.selectedState) {
        this.setSelectedState(to.query.state)
      }
    },
    statePassedAsProp (to) {
      this.selectedState = to
    }
  }

}
</script>

<style lang="scss" scoped>
@import '@/scss/org-state-select.scss';
@import '@/scss/lsc-dot-gov.scss';
</style>